import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { toast } from 'react-hot-toast';
import { Building2, ArrowLeft, User } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { useAuth } from '@/app/providers';
import { useCustomer } from '@/app/providers';
import { CreateOrganization } from '@/models/userOrganization';
import { OrgaConfigurationRepository } from '@/services/firebase/orga-configuration-repository';
import { UserAffiliationRepository } from '@/services/firebase/user-affiliation-repository';
import { CustomerService } from '@/services/firebase/customer-service';
import PhoneInput from '@/features/order-journey/components/PhoneInput';
import { useLoadScript, Libraries } from "@react-google-maps/api";
import { GOOGLE_MAPS_API_KEY } from '@/shared/config/environment';
import { GooglePlacesSearchModern } from '@/components/shared/GooglePlacesSearchModern';
import SeoTags from '@/components/shared/SeoTags';
import { CONTACT_POSITIONS } from '@/shared/constants/contact';

const libraries: Libraries = ["places"];

// Retirer getContactIcon inutilisé

// Types for the form (extended from CreateOrganization)
interface CreateOrganizationFormValues extends Omit<CreateOrganization, 'phone' | 'whatsappNumber' | 'instagramPage' | 'facebookPage' | 'tiktokPage'> {
  phone: string; // Keep for backward compatibility/validation logic in existing code
  whatsappNumber?: string; // Optional
  contactName?: string;
  contactPosition?: string;
  legalInfo?: {
    companyName?: string;
    legalId?: string;
    vatNumber?: string;
  };
}

// Utiliser le nouveau composant GooglePlacesSearch avec la nouvelle API
const GooglePlacesSearch = GooglePlacesSearchModern;

const CreateOrganizationPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { currentUser, refreshPermissions } = useAuth();
  const { refreshCustomer } = useCustomer();
  const [isLoading, setIsLoading] = useState(false);
  const [isInitializing, setIsInitializing] = useState(true);
  const [addressValue, setAddressValue] = useState('');
  const [phoneValue, setPhoneValue] = useState('');
  const [generatedId, setGeneratedId] = useState('');
  const [isCheckingId, setIsCheckingId] = useState(false);
  const [idExists, setIdExists] = useState(false);

  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: GOOGLE_MAPS_API_KEY || "",
    libraries,
  });

  // Tous les utilisateurs connectés peuvent créer une organisation
  useEffect(() => {
    if (currentUser) {
      setIsInitializing(false);
    }
  }, [currentUser]);

  // Handler pour la sélection d'adresse Google Places
  const handleAddressSelect = (address: string, city: string, postalCode: string, country: string, _houseNumber: string) => {
    form.setValue('address.street', address);
    form.setValue('address.city', city);
    form.setValue('address.postalCode', postalCode);
    form.setValue('address.country', country);
    setAddressValue(`${address}, ${city} ${postalCode}`);
  };

  const handleGoogleMapsSuccess = () => {
    console.log('✅ Parent: Google Maps loaded successfully');
  };

  const handleGoogleMapsFailure = () => {
    console.log('❌ Parent: Google Maps failed to load');
  };

  // Handler pour le changement de téléphone
  const handlePhoneChange = (phone: string) => {
    setPhoneValue(phone);
    form.setValue('phone', phone);
  };

  // Fonction pour générer l'ID d'organisation à partir du nom
  const generateOrganizationId = (name: string): string => {
    return name.toLowerCase()
      .trim()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '') // Retirer les accents
      .replace(/[^a-z0-9]+/g, '-') // Remplacer les caractères spéciaux par des tirets
      .replace(/^-+|-+$/g, ''); // Retirer les tirets au début et à la fin
  };

  // Vérifier si l'ID existe déjà dans Firestore
  const checkOrganizationIdExists = async (orgId: string): Promise<boolean> => {
    if (!orgId) return false;

    try {
      const existingOrg = await OrgaConfigurationRepository.getByOrganizationId(orgId);
      return !!existingOrg;
    } catch (error) {
      console.error('Erreur lors de la vérification de l\'ID:', error);
      return false;
    }
  };

  const form = useForm<CreateOrganizationFormValues>({
    mode: 'onChange',
    defaultValues: {
      name: '',
      description: '',
      industry: '',
      phone: '',
      whatsappNumber: '',
      address: {
        street: '',
        city: '',
        postalCode: '',
        country: 'FR',
      },
      legalInfo: {
        companyName: '',
        legalId: '',
        vatNumber: '',
      },
      creatorEmail: currentUser?.email || '',
      contactName: currentUser?.displayName || '',
      contactPosition: ''
    }
  });

  // Surveiller le champ nom pour générer l'ID automatiquement
  const watchName = form.watch('name');

  useEffect(() => {
    const checkId = async () => {
      if (!watchName || watchName.trim().length === 0) {
        setGeneratedId('');
        setIdExists(false);
        return;
      }

      const orgId = generateOrganizationId(watchName);
      setGeneratedId(orgId);

      // Vérifier si l'ID existe déjà
      setIsCheckingId(true);
      const exists = await checkOrganizationIdExists(orgId);
      setIdExists(exists);
      setIsCheckingId(false);
    };

    const timer = setTimeout(() => {
      checkId();
    }, 500);

    return () => clearTimeout(timer);
  }, [watchName]);

  // Vérifier si tous les champs requis sont remplis
  const watchAllFields = form.watch();
  const isFormValid =
    watchAllFields.name &&
    watchAllFields.name.trim().length >= 2 &&
    watchAllFields.industry &&
    watchAllFields.industry.length > 0 &&
    !idExists &&
    !isCheckingId &&
    generatedId &&
    watchAllFields.phone &&
    watchAllFields.phone.trim().length > 0 &&
    watchAllFields.address?.street &&
    watchAllFields.address?.street.trim().length > 0 &&
    watchAllFields.address?.city &&
    watchAllFields.address?.city.trim().length > 0 &&
    watchAllFields.address?.postalCode &&
    watchAllFields.address?.postalCode.trim().length > 0 &&
    watchAllFields.address?.country &&
    watchAllFields.address?.country.trim().length > 0 &&
    watchAllFields.creatorEmail &&
    watchAllFields.creatorEmail.trim().length > 0 &&
    watchAllFields.contactName &&
    watchAllFields.contactName.trim().length > 0 &&
    watchAllFields.contactPosition &&
    watchAllFields.contactPosition.trim().length > 0 &&
    watchAllFields.legalInfo?.companyName &&
    watchAllFields.legalInfo?.companyName.trim().length > 0 &&
    watchAllFields.legalInfo?.legalId &&
    watchAllFields.legalInfo?.legalId.trim().length > 0;

  const completionPercentage = useMemo(() => {
    let completed = 0;
    const total = 9; // Augmenté de 7 à 9 pour inclure les 2 champs légaux requis

    const name = watchAllFields.name || '';
    const industry = watchAllFields.industry || '';
    const contactName = watchAllFields.contactName || '';
    const contactPosition = watchAllFields.contactPosition || '';
    const creatorEmail = watchAllFields.creatorEmail || '';
    const phone = watchAllFields.phone || '';
    const address = watchAllFields.address || { street: '', city: '', postalCode: '', country: '' };
    const legalInfo = watchAllFields.legalInfo || { companyName: '', legalId: '' };

    if (name.trim().length >= 2) completed++;
    if (industry.length > 0) completed++;
    if (contactName.trim().length > 0) completed++;
    if (contactPosition.trim().length > 0) completed++;
    if (creatorEmail.trim().length > 0) completed++;
    if (phone.trim().length > 0) completed++;

    if (address.street && address.street.trim().length > 0 &&
      address.city && address.city.trim().length > 0 &&
      address.postalCode && address.postalCode.trim().length > 0 &&
      address.country && address.country.trim().length > 0) {
      completed++;
    }

    // Champs légaux
    if (legalInfo.companyName && legalInfo.companyName.trim().length > 0) completed++;
    if (legalInfo.legalId && legalInfo.legalId.trim().length > 0) completed++;

    return Math.min(100, Math.round((completed / total) * 100));
  }, [watchAllFields]);

  const getProgressMessage = (pct: number) => {
    if (pct === 100) return "Tout est prêt ! 🚀";
    if (pct >= 75) return "Presque fini !";
    if (pct >= 50) return "Bonne progression !";
    if (pct >= 25) return "Bon début !";
    return "C'est parti !";
  };

  const onSubmit = async (data: CreateOrganizationFormValues) => {
    if (!currentUser) {
      toast.error(t('organizations.create.mustBeLoggedIn'));
      return;
    }

    if (idExists) {
      toast.error(t('organizations.create.idAlreadyExists'));
      return;
    }

    if (!generatedId) {
      toast.error(t('organizations.create.invalidId'));
      return;
    }

    setIsLoading(true);
    try {
      const finalCheck = await checkOrganizationIdExists(generatedId);
      if (finalCheck) {
        toast.error(t('organizations.create.idAlreadyExists'));
        setIsLoading(false);
        return;
      }

      const organizationId = generatedId;
      const savContacts: Array<{ type: 'email' | 'phone' | 'whatsapp', value: string, label?: string }> = [];
      const socials: Record<string, string> = {};
      const internalContacts: Array<{ name: string; email?: string; phone?: string; position?: string; }> = [];

      if (data.creatorEmail || data.phone) {
        internalContacts.push({
          name: data.contactName || 'Contact Principal',
          email: data.creatorEmail,
          phone: data.phone,
          position: data.contactPosition || 'Responsable'
        });
      }

      const configuration = {
        organizationId,
        name: data.name,
        description: data.description || '',
        industry: data.industry || '',
        logo: '',
        address: data.address,
        phone: data.phone,
        email: data.creatorEmail,
        website: '',
        contacts: internalContacts,
        afterSalesService: {
          isSavChatEnabled: false,
          contacts: savContacts,
          description: {
            fr: '',
            en: '',
            es: '',
          }
        },
        socials: socials,
        legalInfo: data.legalInfo,
        isActive: true,
        isStripeEnabled: false,
        paymentMode: 'none' as const,
        shippingConfig: {
          methods: [],
        },
        createdAt: new Date(),
        updatedAt: new Date(),
      };

      await OrgaConfigurationRepository.create(configuration);

      const customer = await CustomerService.findOrCreateCustomer(
        currentUser.email || '',
        currentUser.uid
      );

      await UserAffiliationRepository.create({
        firebaseUid: currentUser.uid, // ✅ Requis pour l'ID structuré
        customerId: customer.id,
        email: currentUser.email || undefined,
        organizationId,
        roleName: 'owner',
        status: 'active',
        invitedBy: currentUser.uid,
      });

      await refreshCustomer();
      await refreshPermissions();

      toast.success(t('organizations.create.success'));
      navigate(`/orga/${organizationId}/admin/configuration`);

    } catch (error) {
      console.error('Erreur lors de la création de l\'espace de vente:', error);
      toast.error(t('organizations.create.error'));
    } finally {
      setIsLoading(false);
    }
  };

  const inputClasses = "w-full px-4 py-3 border border-border-medium rounded-xl focus:outline-none focus:ring-4 focus:ring-primary-10 focus:border-primary transition-all duration-200 bg-background-secondary hover:bg-surface placeholder:text-foreground-secondary text-sm text-foreground";
  const labelClasses = "block text-sm font-semibold text-foreground mb-2";
  const sectionClasses = "bg-surface rounded-2xl shadow-sm border border-border-light p-8";
  const sectionTitleClasses = "text-lg font-bold text-foreground flex items-center gap-3 mb-6";

  if (isInitializing) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background-secondary py-12 px-4 sm:px-6 lg:px-8">
      <SeoTags
        title={`Créer une organisation | CashLive`}
        description={`Créez votre espace de vente CashLive et configurez les informations clés.`}
        url={typeof window !== 'undefined' ? window.location.href : ''}
        noIndex={true}
      />
      <div className="w-full">
        <div className="mb-8">
          <button
            onClick={() => navigate('/organizations/manage')}
            className="group flex items-center text-foreground-secondary hover:text-foreground transition-colors mb-6"
          >
            <div className="p-1.5 rounded-lg group-hover:bg-surface group-hover:shadow-sm transition-all mr-2">
              <ArrowLeft className="w-4 h-4" />
            </div>
            <span className="font-medium">{t('organizations.create.backToOrganizations')}</span>
          </button>

          <div className="flex items-center gap-5">
            <div className="p-4 bg-gradient-to-br from-primary to-primary-dark rounded-2xl shadow-lg shadow-primary-20 text-white">
              <Building2 className="w-8 h-8" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-foreground tracking-tight">
                {t('organizations.create.title')}
              </h1>
              <p className="text-foreground-secondary mt-1 text-lg">Configurez votre nouvel espace de vente en quelques étapes</p>
            </div>
          </div>
        </div>

        <div className="flex flex-col lg:flex-row gap-8 items-start relative">
          <div className="flex-1 w-full min-w-0">
            {/* Mobile Progress Bar */}
            <div className="lg:hidden fixed top-16 left-0 right-0 z-40 px-4">
              <div className="max-w-5xl mx-auto bg-surface rounded-xl p-4 shadow-sm border border-border-light">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-xs font-bold text-primary uppercase">Progression</span>
                  <span className="text-sm font-bold text-foreground">{completionPercentage}%</span>
                </div>
                <div className="w-full bg-background-tertiary rounded-full h-2">
                  <div className="bg-primary h-2 rounded-full transition-all duration-500" style={{ width: `${completionPercentage}%` }}></div>
                </div>
              </div>
            </div>

            <form onSubmit={form.handleSubmit(onSubmit)} autoComplete="off" className="space-y-8 mt-24 lg:mt-0">

              {/* Section: Informations Générales */}
              <div className={sectionClasses}>
                <h3 className={sectionTitleClasses}>
                  <div className="p-2 bg-primary-10 text-primary rounded-lg">
                    <Building2 className="w-5 h-5" />
                  </div>
                  Informations Générales
                </h3>

                <div className="space-y-6">
                  <div>
                    <label htmlFor="name" className={labelClasses}>
                      {t('organizations.create.name')} *
                    </label>
                    <input
                      id="name"
                      type="text"
                      {...form.register('name')}
                      className={inputClasses}
                      placeholder={t('organizations.create.namePlaceholder')}
                    />
                    {generatedId && (
                      <div className="mt-2 flex items-center gap-2 text-sm">
                        <span className="text-foreground-secondary font-mono bg-background-secondary px-2 py-0.5 rounded border border-border-light">
                          ID: {generatedId}
                        </span>
                        {isCheckingId ? (
                          <span className="flex items-center text-yellow-600 text-xs">
                            <svg className="animate-spin h-3 w-3 mr-1.5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            {t('organizations.create.checking')}
                          </span>
                        ) : idExists ? (
                          <span className="text-error text-xs font-medium flex items-center">
                            <span className="w-1.5 h-1.5 rounded-full bg-error mr-1.5"></span>
                            {t('organizations.create.alreadyUsed')}
                          </span>
                        ) : (
                          <span className="text-success text-xs font-medium flex items-center">
                            <span className="w-1.5 h-1.5 rounded-full bg-success mr-1.5"></span>
                            {t('organizations.create.available')}
                          </span>
                        )}
                      </div>
                    )}
                    {form.formState.errors.name && (
                      <p className="mt-1 text-sm text-red-600">{form.formState.errors.name.message}</p>
                    )}
                  </div>

                  <div>
                    <label htmlFor="description" className={labelClasses}>
                      {t('organizations.create.description')}
                    </label>
                    <textarea
                      id="description"
                      {...form.register('description')}
                      rows={3}
                      className={inputClasses}
                      placeholder={t('organizations.create.descriptionPlaceholder')}
                    />
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="industry" className={labelClasses}>
                        {t('organizations.create.industry')} *
                      </label>
                      <div className="relative">
                        <select
                          id="industry"
                          {...form.register('industry', { required: true })}
                          className={`${inputClasses} appearance-none`}
                        >
                          <option value="">{t('organizations.create.industries.select')}</option>
                          <option value="retail">{t('organizations.create.industries.retail')}</option>
                          <option value="ecommerce">{t('organizations.create.industries.ecommerce')}</option>
                          <option value="fashion">{t('organizations.create.industries.fashion')}</option>
                          <option value="beauty">{t('organizations.create.industries.beauty')}</option>
                          <option value="food">{t('organizations.create.industries.food')}</option>
                          <option value="technology">{t('organizations.create.industries.technology')}</option>
                          <option value="healthcare">{t('organizations.create.industries.healthcare')}</option>
                          <option value="education">{t('organizations.create.industries.education')}</option>
                          <option value="consulting">{t('organizations.create.industries.consulting')}</option>
                          <option value="realEstate">{t('organizations.create.industries.realEstate')}</option>
                          <option value="finance">{t('organizations.create.industries.finance')}</option>
                          <option value="hospitality">{t('organizations.create.industries.hospitality')}</option>
                          <option value="entertainment">{t('organizations.create.industries.entertainment')}</option>
                          <option value="sports">{t('organizations.create.industries.sports')}</option>
                          <option value="automotive">{t('organizations.create.industries.automotive')}</option>
                          <option value="manufacturing">{t('organizations.create.industries.manufacturing')}</option>
                          <option value="construction">{t('organizations.create.industries.construction')}</option>
                          <option value="transport">{t('organizations.create.industries.transport')}</option>
                          <option value="energy">{t('organizations.create.industries.energy')}</option>
                          <option value="agriculture">{t('organizations.create.industries.agriculture')}</option>
                          <option value="other">{t('organizations.create.industries.other')}</option>
                        </select>
                        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-4 text-foreground-secondary">
                          <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path></svg>
                        </div>
                      </div>
                    </div>

                  </div>
                </div>
              </div>

              {/* Section: Contact Principal */}
              <div className={sectionClasses}>
                <h3 className={sectionTitleClasses}>
                  <div className="p-2 bg-primary-10 text-primary rounded-lg">
                    <User className="w-5 h-5" />
                  </div>
                  Contact Principal
                </h3>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div className="col-span-1 sm:col-span-2">
                    <label htmlFor="contactName" className={labelClasses}>
                      Nom complet *
                    </label>
                    <input
                      id="contactName"
                      type="text"
                      {...form.register('contactName', { required: true })}
                      className={inputClasses}
                      placeholder="Jean Dupont"
                    />
                  </div>

                  <div>
                    <label htmlFor="contactPosition" className={labelClasses}>
                      Poste *
                    </label>
                    <div className="relative">
                      <select
                        id="contactPosition"
                        {...form.register('contactPosition', { required: true })}
                        className={`${inputClasses} appearance-none`}
                      >
                        <option value="">Sélectionnez un poste</option>
                        {CONTACT_POSITIONS.map((position) => (
                          <option key={position.value} value={position.value}>
                            {position.label}
                          </option>
                        ))}
                      </select>
                      <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-4 text-foreground-secondary">
                        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path></svg>
                      </div>
                    </div>
                  </div>

                  <div>
                    <label htmlFor="email" className={labelClasses}>
                      {t('admin.configuration.general.email')} *
                    </label>
                    <input
                      id="email"
                      type="email"
                      {...form.register('creatorEmail', { required: true })}
                      className={inputClasses}
                      placeholder="contact@example.com"
                    />
                  </div>

                  <div className="col-span-1 sm:col-span-2">
                    <label htmlFor="phone" className={labelClasses}>
                      {t('organizations.create.phone')} *
                    </label>
                    <PhoneInput
                      value={phoneValue}
                      onChange={handlePhoneChange}
                      placeholder="+33 1 23 45 67 89"
                      personalization={{
                        input: {
                          borderRadius: 'rounded-xl', // Correspondre au style de la page
                        },
                      }}
                    />
                  </div>
                </div>
              </div>

              {/* Section: Informations légales */}
              <div className={sectionClasses}>
                <h3 className={sectionTitleClasses}>
                  <div className="p-2 bg-warning-10 text-warning rounded-lg">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 6l3 1m0 0l-3 9a5.002 5.002 0 006.001 0M6 7l3 9M6 7l6-2m6 2l3-1m-3 1l-3 9a5.002 5.002 0 006.001 0M18 7l3 9m-3-9l-6-2m0-2v2m0 16V5m0 16H9m3 0h3" />
                    </svg>
                  </div>
                  Informations légales
                </h3>

                <div className="bg-info-10 border border-info-20 rounded-xl p-4 mb-6">
                  <p className="text-sm text-info">
                    ℹ️ Les documents légaux (Mentions légales, CGV, Politique de confidentialité) seront générés automatiquement à partir de ces informations.
                  </p>
                </div>

                <div className="space-y-6">
                  <div>
                    <label htmlFor="companyName" className={labelClasses}>
                      Nom légal de l'entreprise *
                    </label>
                    <input
                      id="companyName"
                      type="text"
                      {...form.register('legalInfo.companyName', { required: true })}
                      className={inputClasses}
                      placeholder="Ex: Ma Société SAS"
                    />
                  </div>

                  <div>
                    <label htmlFor="legalId" className={labelClasses}>
                      Identifiant légal / Numéro d'enregistrement *
                    </label>
                    <input
                      id="legalId"
                      type="text"
                      {...form.register('legalInfo.legalId', { required: true })}
                      className={inputClasses}
                      placeholder="Ex: SIRET (FR), VAT (EU), Company Number..."
                    />
                    <p className="text-xs text-foreground-secondary mt-1">
                      SIRET pour la France, VAT pour l'UE, Company Number pour le UK, etc.
                    </p>
                  </div>

                  <div>
                    <label htmlFor="vatNumber" className={labelClasses}>
                      Numéro de TVA <span className="text-foreground-secondary font-normal">(optionnel)</span>
                    </label>
                    <input
                      id="vatNumber"
                      type="text"
                      {...form.register('legalInfo.vatNumber')}
                      className={inputClasses}
                      placeholder="Ex: FR12345678901"
                    />
                  </div>

                  {/* Adresse du siège - maintenant directement après le numéro de TVA */}
                  <div className="pt-4">
                    <h4 className="text-base font-semibold text-foreground mb-4">
                      Adresse du siège
                    </h4>

                    <div className="space-y-6">
                      <div>
                        <label htmlFor="address" className={labelClasses}>
                          Rechercher une adresse *
                        </label>
                        <GooglePlacesSearch
                          isLoaded={isLoaded}
                          loadError={loadError}
                          onAddressSelect={handleAddressSelect}
                          placeholder={t('organizations.create.addressPlaceholder')}
                          autocompleteText={t('organizations.create.addressAutocomplete')}
                          value={addressValue}
                          onChange={setAddressValue}
                          className={inputClasses}
                          onGoogleMapsLoadSuccess={handleGoogleMapsSuccess}
                          onGoogleMapsLoadFailure={handleGoogleMapsFailure}
                        />
                      </div>

                      {/* Affichage des champs d'adresse extraits - en lecture seule */}
                      {(form.watch('address.street') || form.watch('address.city') || form.watch('address.postalCode') || form.watch('address.country')) && (
                        <div className="grid grid-cols-1 sm:grid-cols-6 gap-6 animate-in fade-in slide-in-from-top-2 duration-300 pt-2">
                          <div className="sm:col-span-6">
                            <label htmlFor="street" className={labelClasses}>
                              Rue *
                            </label>
                            <input
                              id="street"
                              type="text"
                              {...form.register('address.street')}
                              className={`w-full px-4 py-3 rounded-xl border cursor-not-allowed placeholder:text-foreground-secondary text-sm focus:outline-none focus:ring-4 focus:ring-primary-10 focus:border-primary`}
                              style={{ backgroundColor: 'var(--background-tertiary)', color: 'var(--foreground)', borderColor: 'var(--border-medium)' }}
                              autoComplete="off"
                              placeholder="123 Rue de l'Exemple"
                              readOnly
                            />
                          </div>

                          <div className="sm:col-span-2">
                            <label htmlFor="postalCode" className={labelClasses}>
                              Code postal *
                            </label>
                            <input
                              id="postalCode"
                              type="text"
                              {...form.register('address.postalCode')}
                              className={`w-full px-4 py-3 rounded-xl border cursor-not-allowed placeholder:text-foreground-secondary text-sm focus:outline-none focus:ring-4 focus:ring-primary-10 focus:border-primary`}
                              style={{ backgroundColor: 'var(--background-tertiary)', color: 'var(--foreground)', borderColor: 'var(--border-medium)' }}
                              autoComplete="off"
                              placeholder="75000"
                              readOnly
                            />
                          </div>

                          <div className="sm:col-span-2">
                            <label htmlFor="city" className={labelClasses}>
                              Ville *
                            </label>
                            <input
                              id="city"
                              type="text"
                              {...form.register('address.city')}
                              className={`w-full px-4 py-3 rounded-xl border cursor-not-allowed placeholder:text-foreground-secondary text-sm focus:outline-none focus:ring-4 focus:ring-primary-10 focus:border-primary`}
                              style={{ backgroundColor: 'var(--background-tertiary)', color: 'var(--foreground)', borderColor: 'var(--border-medium)' }}
                              autoComplete="off"
                              placeholder="Paris"
                              readOnly
                            />
                          </div>

                          <div className="sm:col-span-2">
                            <label htmlFor="country" className={labelClasses}>
                              Pays *
                            </label>
                            <input
                              id="country"
                              type="text"
                              {...form.register('address.country')}
                              className={`w-full px-4 py-3 rounded-xl border cursor-not-allowed placeholder:text-foreground-secondary text-sm focus:outline-none focus:ring-4 focus:ring-primary-10 focus:border-primary`}
                              style={{ backgroundColor: 'var(--background-tertiary)', color: 'var(--foreground)', borderColor: 'var(--border-medium)' }}
                              autoComplete="off"
                              placeholder="France"
                              readOnly
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Boutons */}
              <div className="flex items-center justify-end space-x-4 pt-4 border-t border-border-medium mt-8">
                <button
                  type="submit"
                  disabled={isLoading || !isFormValid}
                  className="px-8 py-3 bg-primary text-white font-medium rounded-xl hover:bg-primary-dark hover:shadow-lg hover:shadow-primary-20 transition-all focus:outline-none focus:ring-4 focus:ring-primary-10 disabled:opacity-50 disabled:cursor-not-allowed disabled:shadow-none"
                  title={!isFormValid ? t('organizations.create.fillRequired') : ''}
                >
                  {isLoading ? t('organizations.create.submitting') : t('organizations.create.submit')}
                </button>
              </div>
            </form>
          </div>

          {/* Vertical Progress Bar (Desktop) */}
          <div className="hidden lg:block w-80 sticky top-24 h-fit">
            <div className="bg-surface rounded-2xl p-6 shadow-sm border border-border-light relative">
              <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-primary to-primary-dark"></div>

              <h3 className="font-bold text-foreground mb-6">Progression</h3>

              <div className="flex flex-col items-center justify-center py-4">
                <div className="relative w-32 h-32 flex items-center justify-center">
                  {/* Circular Progress SVG */}
                  <svg className="transform -rotate-90 w-32 h-32">
                    <circle
                      className="text-background-tertiary"
                      strokeWidth="8"
                      stroke="currentColor"
                      fill="transparent"
                      r="58"
                      cx="64"
                      cy="64"
                    />
                    <circle
                      className="text-primary transition-all duration-1000 ease-out"
                      strokeWidth="8"
                      strokeDasharray={364}
                      strokeDashoffset={364 - (364 * completionPercentage) / 100}
                      strokeLinecap="round"
                      stroke="currentColor"
                      fill="transparent"
                      r="58"
                      cx="64"
                      cy="64"
                    />
                  </svg>
                  <div className="absolute inset-0 flex flex-col items-center justify-center text-foreground">
                    <span className="text-3xl font-bold">{completionPercentage}%</span>
                  </div>
                </div>
                <p className="text-center font-medium text-primary mt-4">{getProgressMessage(completionPercentage)}</p>
                <p className="text-center text-xs text-foreground-secondary mt-1">Remplissez les champs obligatoires</p>
              </div>

              <div className="mt-6 space-y-3">
                <div className={`flex items-center gap-3 p-3 rounded-xl transition-colors ${watchAllFields.name ? 'bg-success-10 text-success-dark' : 'bg-background-tertiary text-foreground-secondary'}`}>
                  <div className={`w-5 h-5 rounded-full flex items-center justify-center border ${watchAllFields.name ? 'border-success bg-success text-white' : 'border-border-medium'}`}>
                    {watchAllFields.name && <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" /></svg>}
                  </div>
                  <span className="text-sm font-medium">Informations</span>
                </div>
                <div className={`flex items-center gap-3 p-3 rounded-xl transition-colors ${watchAllFields.contactName ? 'bg-success-10 text-success-dark' : 'bg-background-tertiary text-foreground-secondary'}`}>
                  <div className={`w-5 h-5 rounded-full flex items-center justify-center border ${watchAllFields.contactName ? 'border-success bg-success text-white' : 'border-border-medium'}`}>
                    {watchAllFields.contactName && <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" /></svg>}
                  </div>
                  <span className="text-sm font-medium">Contact</span>
                </div>
                <div className={`flex items-center gap-3 p-3 rounded-xl transition-colors ${(watchAllFields.legalInfo?.companyName && watchAllFields.address?.city) ? 'bg-success-10 text-success-dark' : 'bg-background-tertiary text-foreground-secondary'}`}>
                  <div className={`w-5 h-5 rounded-full flex items-center justify-center border ${(watchAllFields.legalInfo?.companyName && watchAllFields.address?.city) ? 'border-success bg-success text-white' : 'border-border-medium'}`}>
                    {(watchAllFields.legalInfo?.companyName && watchAllFields.address?.city) && <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" /></svg>}
                  </div>
                  <span className="text-sm font-medium">Informations légales</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateOrganizationPage;
